// dependencies
import React, { useMemo, useState } from "react";
import { AutoSizer, Column, Table } from "react-virtualized";

import { faArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Checkbox from "../../../../../../components/Inputs/Checkbox";
import InputText from "../../../../../../components/Inputs/InputText";
import Spacer from "../../../../../../components/Spacer";
import Typography from "../../../../../../components/Text";
import { CheckBoxCell, QuestionCell, QuestionTypeCell, ReviewFormCell } from "./TableCells";

// store
import useReportStore from "../../store";

// templates
import EmptySearchTableMessage from "../../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
// styles
import css from "./UniqueQuestionsTable.module.scss";
// types
import { SortParam } from "./schema";

import "react-virtualized/styles.css";

/**
 * Formats question data and renders it for the table
 * handles filtering results based on state of selections
 * or results from search input
 *
 * Only questions of the same type may be grouped together
 *
 * 'Show Selected' button toggles whether or not to show only selected questions
 *
 * FuzzySearch runs on the results of type filtered questions if a questions is selected
 * and on all questions if no questions are selected
 */
const UniqueQuestionsTable = () => {
	const [
		toggleSelectedQuestion,
		selectedQuestionIDs,
		showOnlySelectedQuestions,
		setQuestionGroupModalValue,
		getUniqueQuestions,
		hasGroupableQuestions,
		report,
		getUniqueSurveyQuestions,
	] = useReportStore((state) => [
		state.questionGroupingModal.toggleSelectedQuestion,
		state.questionGroupingModal.selectedQuestions,
		state.questionGroupingModal.showOnlySelectedQuestions,
		state.questionGroupingModal.setQuestionGroupModalValue,
		state.questionGroupingModal.getUniqueQuestions,
		state.hasGroupableQuestions,
		state.report,
		state.getUniqueSurveyQuestions,
	]);

	// default sorts from the requirements
	const defaultSort: SortParam[] = [
		{ sortBy: "question", sortDirection: "asc" },
		{ sortBy: "reviewForm", sortDirection: "asc" },
	];
	const [searchInput, setSearchInput] = useState("");
	const [sorts, setSorts] = useState<SortParam[]>(defaultSort);

	const tableColumns = {
		checkbox: {
			label: "",
			dataKey: "checkbox",
			columnWidth: 25,
			cellRenderer: CheckBoxCell,
			flexGrow: 0,
		},
		question: {
			label: "Question",
			dataKey: "question",
			columnWidth: 300,
			cellRenderer: QuestionCell,
			flexGrow: 1,
		},
		reviewForm: {
			label: "Review Form",
			dataKey: "reviewForm",
			columnWidth: 200,
			cellRenderer: ReviewFormCell,
			flexGrow: 1,
		},
		questionType: {
			label: "Question Type",
			dataKey: "questionType",
			columnWidth: 200,
			cellRenderer: QuestionTypeCell,
			flexGrow: 1,
		},
	};

	// table columns for grouping questions modal
	const columnsToDisplay = ["checkbox", "question", "reviewForm", "questionType"];

	const rowsToDisplay =
		report?.type === "reviewReport" ? getUniqueQuestions(searchInput) : getUniqueSurveyQuestions(searchInput);
	// handle sort questions
	const sortRows = (rows, sortParams) => {
		const sortedList = [...rows];
		sortedList.sort((a, b) => {
			for (const { sortBy, sortDirection } of sortParams) {
				if (a[sortBy] !== b[sortBy]) {
					const modifier = sortDirection === "asc" ? 1 : -1;
					return a[sortBy] > b[sortBy] ? modifier : -modifier;
				}
			}
			return 0;
		});
		return sortedList;
	};

	const handleSort = (dataKey) => {
		// cast sort by since dataKey can be an object or string
		const sortBy = typeof dataKey === "object" && dataKey !== null ? dataKey.sortBy : dataKey;

		setSorts((prevSorts) => {
			// find existing sort to handle toggle, add new sort if not found
			const existingSortParamIndex = prevSorts.findIndex((param) => param.sortBy === sortBy);
			const newSortParams = [...prevSorts];
			// sort order toggle: asc -> desc -> none
			if (existingSortParamIndex > -1) {
				const currentSortDirection = newSortParams[existingSortParamIndex].sortDirection;
				if (currentSortDirection === "asc") {
					newSortParams[existingSortParamIndex] = { sortBy, sortDirection: "desc" };
				} else if (currentSortDirection === "desc") {
					newSortParams.splice(existingSortParamIndex, 1); // remove sort param
				}
			} else {
				// If not currently sorted, set as ascending
				newSortParams.push({ sortBy, sortDirection: "asc" });
			}
			// If shift key is held, keep previous sorts for other columns else clear them
			return dataKey.event.shiftKey ? newSortParams : newSortParams.filter((param) => param.sortBy === sortBy);
		});
	};

	const headerRenderer = ({ dataKey, label }) => {
		const sortParam = sorts.find((param) => param.sortBy === dataKey);
		const direction = sortParam ? (
			sortParam.sortDirection === "asc" ? (
				<FontAwesomeIcon icon={faArrowUp} />
			) : (
				<FontAwesomeIcon rotation={180} icon={faArrowUp} />
			)
		) : (
			""
		);
		return (
			<div>
				{label} {direction}
			</div>
		);
	};

	const sortedRows = useMemo(() => sortRows(rowsToDisplay, sorts), [rowsToDisplay, sorts]);

	const emptyTableState = searchInput ? (
		<div className={css.emptySearch}>
			<EmptySearchTableMessage />
		</div>
	) : (
		<Typography size="paragraph-2" weight="semi-bold">
			No Questions found.
		</Typography>
	);

	const handleSearchBarChange = (searchInput: string) => {
		setSearchInput(searchInput);
		setSorts(searchInput ? [] : defaultSort);
	};

	return (
		<div>
			<div className={css.tableTop}>
				<span className={css.questionsSearch}>
					<InputText
						type="text"
						placeholder="Search Questions..."
						value={searchInput}
						onChange={(searchInput) => handleSearchBarChange(searchInput)}
					/>
				</span>
				<div className={css.showSelectedCheckbox}>
					<label className={css.checkboxLabel} htmlFor="showSelected">
						Show Selected Only
					</label>
					<Checkbox
						id="showSelected"
						value={showOnlySelectedQuestions}
						onChange={() =>
							setQuestionGroupModalValue({
								showOnlySelectedQuestions: !showOnlySelectedQuestions,
							})
						}
					/>
				</div>
			</div>

			{!hasGroupableQuestions ? (
				<>
					<Spacer height={2} />
					<div className={css.noGroupableQuestions}>There are no questions available for grouping.</div>
				</>
			) : null}

			{hasGroupableQuestions ? (
				<div className={`${css.reviewReportQuestionsTable} ${css.modalTable}`}>
					<AutoSizer>
						{({ width, height }) => (
							<Table
								className={`${css.ReactVirtualized__Table__headerRow} ${css.ReactVirtualized__Table__rowColumn}`}
								width={width}
								height={height}
								headerHeight={40}
								rowHeight={50}
								rowCount={rowsToDisplay.length}
								sort={handleSort}
								rowGetter={({ index }) => {
									const questionData = sortedRows[index];

									return {
										questionData,
										toggleSelectedQuestion,
										selectedQuestionIDs,
										disabled: false,
									};
								}}
							>
								{columnsToDisplay.map((column) => {
									const { label, dataKey, columnWidth, cellRenderer, flexGrow } =
										tableColumns[column];
									return (
										<Column
											label={label}
											dataKey={dataKey}
											key={dataKey}
											width={columnWidth}
											cellDataGetter={({ rowData }) => rowData}
											cellRenderer={cellRenderer}
											flexGrow={flexGrow}
											headerRenderer={headerRenderer}
										/>
									);
								})}
							</Table>
						)}
					</AutoSizer>
				</div>
			) : null}

			{rowsToDisplay.length < 1 && emptyTableState}
		</div>
	);
};

export default UniqueQuestionsTable;
